/**
 * The books a result card shows for one line — one list for every count and
 * best price on the card (hero, line rows, books panel, related-market chips),
 * so a line row can't say 71 books while the panel lists 66 (Oct 2026).
 *
 *  - Entries with no data (null / undefined) are dropped.
 *  - Exchange lay feeds ("Betfair Exchange (Lay)") are dropped: a lay price is
 *    what you'd lay at, not a price the user can back.
 *  - Regional variants ("BetMGM (UK)", "Unibet (Australia)") fold into their
 *    brand, keeping the best-priced entry (and its original book key); a
 *    backend-flagged price outlier only wins when the brand has nothing else.
 */
import { isLayBookName, isUnbackableBookData } from './canonicalEv.js';
import { extractPrice } from './betCardFormatters';

/** "BetMGM (UK)" → "BetMGM". */
export const canonicalBookName = (name) => String(name || '').replace(/\s*\([^)]*\)/g, '').trim();

const nonEmpty = (obj) => (obj && typeof obj === 'object' && Object.keys(obj).length > 0 ? obj : null);

/**
 * A line's bookOdds map: the first non-empty of line.bookOdds / line.raw.bookOdds
 * (an empty `{}` is truthy, so a naive `||` chain can stop on it).
 */
export function lineBookOdds(line) {
    if (!line || typeof line !== 'object') return {};
    return nonEmpty(line.bookOdds) || nonEmpty(line.raw?.bookOdds) || {};
}

/** Deduped `[{ book, odds, price, flagged }]` for a bookOdds map, in first-seen brand order. */
export function dedupeCardBooks(bookOdds) {
    const byBrand = new Map();
    for (const [book, odds] of Object.entries(bookOdds || {})) {
        if (odds === null || odds === undefined) continue;
        if (isLayBookName(book)) continue;
        const brand = canonicalBookName(book);
        const price = extractPrice(odds);
        const flagged = isUnbackableBookData(odds, book);
        const existing = byBrand.get(brand);
        // Keep the variant with the better price (higher American = more favorable).
        // A backend-flagged outlier never displaces a clean variant of the same brand,
        // so the brand's shown price is the one the hero's best price can use.
        const better = !existing
            || (existing.flagged && !flagged)
            || (existing.flagged === flagged && price !== null && (existing.price === null || price > existing.price));
        if (better) byBrand.set(brand, { book, odds, price, flagged });
    }
    return [...byBrand.values()];
}

/** Book count for a line (or a bookOdds map) — the number the books panel lists. */
export function cardBookCount(lineOrBookOdds) {
    return dedupeCardBooks(toBookOdds(lineOrBookOdds)).length;
}

/**
 * Best backable price for a line (or a bookOdds map): highest price among the
 * deduped books, skipping backend-flagged price outliers like the hero does.
 * null when no book has a price.
 */
export function cardBestPrice(lineOrBookOdds) {
    let best = null;
    for (const { price, flagged } of dedupeCardBooks(toBookOdds(lineOrBookOdds))) {
        if (typeof price !== 'number' || !Number.isFinite(price)) continue;
        if (flagged) continue;
        if (best === null || price > best) best = price;
    }
    return best;
}

// Fields only a line/option carries; a bare bookOdds map is book → odds.
const LINE_FIELDS = ['bookOdds', 'raw', 'id', 'selection', 'title', 'market', 'lineValue', 'price', 'ev_percent'];

function toBookOdds(lineOrBookOdds) {
    if (!lineOrBookOdds || typeof lineOrBookOdds !== 'object') return {};
    // Never read a line's own numeric fields (price, lineValue) as books.
    if (LINE_FIELDS.some((f) => f in lineOrBookOdds)) return lineBookOdds(lineOrBookOdds);
    return lineOrBookOdds;
}
